/* HeroPrism.js — Quantum cinematic hero: the "Orbital Liquidity Radar".
 *
 * A premium, bloom-lit 3D scene (ESM, real `three` + UnrealBloomPass):
 *   • 5 tilted distance rings (cyan 2% → green 4% → yellow 6% → orange 8% → red 10%)
 *     each with a bright core line, a soft halo, breathing accent dots and a sweeping
 *     "scan" comet that rides the ring.
 *   • Glowing planets orbiting each ring (varied sizes, a couple with Saturn halos),
 *     additive glow sprites synced to position.
 *   • A layered price CORE: bright icosahedron + fresnel shell + rotating corona,
 *     twin pulse rings and a large soft-blue atmosphere heart.
 *   • Travelling-wave order-flow cables (planet→core and planet→planet).
 *   • Starfield, drifting dust and big soft nebula sprites for depth.
 *   • Cinematic choreography: intro camera push-in + staged assembly (rings, then
 *     planets, then cables), gentle auto-rotate + pointer parallax, scroll-driven
 *     dolly-back + fade exit. Real bloom via EffectComposer (auto-disabled on
 *     low-power / reduced-motion).
 *
 * Usage (React):
 *   const ctl = createHeroPrism(mountEl);
 *   ctl.setExit(0..1); ctl.dispose();
 */
import * as THREE from 'three';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';

const RING_COL = ['#22d3ee', '#2fe08a', '#ffe23b', '#ff9326', '#ff2f4d'];
const RING_R = [9, 16.5, 24, 31.5, 39];
const BG = 0x05070e;

const easeOutCubic = (x) => 1 - Math.pow(1 - x, 3);
const easeInOutCubic = (x) => (x < 0.5 ? 4 * x * x * x : 1 - Math.pow(-2 * x + 2, 3) / 2);
const smooth = (a, b, x) => { const t = Math.max(0, Math.min(1, (x - a) / (b - a))); return t * t * (3 - 2 * t); };
function srand(s) { return () => { s = Math.imul(s ^ (s >>> 15), s | 1); s ^= s + Math.imul(s ^ (s >>> 7), s | 61); return ((s ^ (s >>> 14)) >>> 0) / 4294967296; }; }

// soft radial sprite (white; tint via material.color)
function glowTexture() {
  const c = document.createElement('canvas'); c.width = c.height = 160;
  const g = c.getContext('2d');
  const grd = g.createRadialGradient(80, 80, 0, 80, 80, 80);
  grd.addColorStop(0, 'rgba(255,255,255,1)');
  grd.addColorStop(0.18, 'rgba(255,255,255,0.78)');
  grd.addColorStop(0.45, 'rgba(255,255,255,0.20)');
  grd.addColorStop(1, 'rgba(255,255,255,0)');
  g.fillStyle = grd; g.fillRect(0, 0, 160, 160);
  return new THREE.CanvasTexture(c);
}
// streak/corona sprite — a soft anisotropic star for the core
function coronaTexture() {
  const c = document.createElement('canvas'); c.width = c.height = 256;
  const g = c.getContext('2d'); g.translate(128, 128);
  const grd = g.createRadialGradient(0, 0, 0, 0, 0, 128);
  grd.addColorStop(0, 'rgba(255,255,255,0.9)');
  grd.addColorStop(0.5, 'rgba(160,230,255,0.12)');
  grd.addColorStop(1, 'rgba(160,230,255,0)');
  g.fillStyle = grd; g.beginPath(); g.arc(0, 0, 128, 0, 7); g.fill();
  // 4-point soft star streaks
  g.globalCompositeOperation = 'lighter';
  for (let k = 0; k < 4; k++) {
    g.save(); g.rotate((k * Math.PI) / 2);
    const lg = g.createLinearGradient(0, 0, 120, 0);
    lg.addColorStop(0, 'rgba(180,235,255,0.55)');
    lg.addColorStop(1, 'rgba(180,235,255,0)');
    g.fillStyle = lg; g.beginPath();
    g.moveTo(0, -5); g.lineTo(120, 0); g.lineTo(0, 5); g.closePath(); g.fill();
    g.beginPath(); g.moveTo(0, -5); g.lineTo(-120, 0); g.lineTo(0, 5); g.closePath(); g.fill();
    g.restore();
  }
  return new THREE.CanvasTexture(c);
}

export function createHeroPrism(mount) {
  let w = mount.clientWidth || 1, h = mount.clientHeight || 1;
  const rnd = srand(91237);
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const lowPower = window.matchMedia('(pointer: coarse)').matches || Math.min(w, h) < 520;
  const useBloom = !reduce && !lowPower;

  // ---------- renderer ----------
  const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: false, powerPreference: 'high-performance' });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, lowPower ? 1.5 : 2));
  renderer.setSize(w, h);
  renderer.setClearColor(BG, 1);
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 0.9;
  if (THREE.SRGBColorSpace) renderer.outputColorSpace = THREE.SRGBColorSpace;
  mount.appendChild(renderer.domElement);
  renderer.domElement.style.display = 'block';

  const scene = new THREE.Scene();
  scene.fog = new THREE.FogExp2(BG, 0.0034);
  const camera = new THREE.PerspectiveCamera(40, w / h, 0.1, 1200);

  // ---------- post (bloom) ----------
  let composer = null, bloom = null;
  if (useBloom) {
    composer = new EffectComposer(renderer);
    composer.addPass(new RenderPass(scene, camera));
    bloom = new UnrealBloomPass(new THREE.Vector2(w, h), 0.46, 0.5, 0.16);
    bloom.threshold = 0.16; bloom.strength = 0.46; bloom.radius = 0.5;
    composer.addPass(bloom);
    composer.setPixelRatio(renderer.getPixelRatio());
    composer.setSize(w, h);
  }

  const GLOW = glowTexture();
  const CORONA = coronaTexture();
  const group = new THREE.Group();
  group.rotation.x = -0.32;
  scene.add(group);

  const sprite = (parent, tex, color, scale, opacity, rot = 0) => {
    const s = new THREE.Sprite(new THREE.SpriteMaterial({ map: tex, color: new THREE.Color(color), transparent: true, opacity, blending: THREE.AdditiveBlending, depthWrite: false, depthTest: false, rotation: rot }));
    s.scale.set(scale, scale, 1); parent.add(s); return s;
  };
  const addGlow = (parent, color, scale, opacity) => sprite(parent, GLOW, color, scale, opacity);

  // ---------- big soft nebulae (depth + brand wash) ----------
  const nebulae = [
    sprite(scene, GLOW, '#1b6cff', 240, 0, 0), // back blue
    sprite(scene, GLOW, '#7b54ff', 200, 0, 0), // violet
    sprite(scene, GLOW, '#1fb6a0', 180, 0, 0), // mint
  ];
  nebulae[0].position.set(-60, 22, -180);
  nebulae[1].position.set(90, -30, -150);
  nebulae[2].position.set(20, 40, -120);

  // ---------- starfield (two layers) ----------
  function starLayer(n, rMin, rMax, size, col, opa) {
    const geo = new THREE.BufferGeometry(), pos = new Float32Array(n * 3);
    for (let i = 0; i < n; i++) { const a = rnd() * 6.2832, b = Math.acos(2 * rnd() - 1), R = rMin + rnd() * (rMax - rMin); pos[i * 3] = Math.sin(b) * Math.cos(a) * R; pos[i * 3 + 1] = Math.cos(b) * R * 0.82; pos[i * 3 + 2] = Math.sin(b) * Math.sin(a) * R; }
    geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    const pts = new THREE.Points(geo, new THREE.PointsMaterial({ color: new THREE.Color(col), size, transparent: true, opacity: 0, sizeAttenuation: true, depthWrite: false, blending: THREE.AdditiveBlending }));
    pts.userData.baseOpa = opa; scene.add(pts); return pts;
  }
  const starsFar = starLayer(900, 160, 320, 0.55, '#cfe0ff', 0.55);
  const starsNear = starLayer(420, 90, 180, 0.9, '#a9d8ff', 0.85);

  // drifting dust
  const dustN = 260, dGeo = new THREE.BufferGeometry(), dPos = new Float32Array(dustN * 3);
  for (let i = 0; i < dustN; i++) { dPos[i * 3] = (rnd() - 0.5) * 120; dPos[i * 3 + 1] = (rnd() - 0.5) * 30; dPos[i * 3 + 2] = (rnd() - 0.5) * 120; }
  dGeo.setAttribute('position', new THREE.BufferAttribute(dPos, 3));
  const dust = new THREE.Points(dGeo, new THREE.PointsMaterial({ color: 0x6aa6e0, size: 0.4, transparent: true, opacity: 0, blending: THREE.AdditiveBlending, depthWrite: false }));
  group.add(dust);

  // ---------- ground disc (subtle bid/ask hemisphere wash) ----------
  const discGeo = new THREE.CircleGeometry(46, 96);
  const discMat = new THREE.MeshBasicMaterial({ color: 0x0c1830, transparent: true, opacity: 0, blending: THREE.AdditiveBlending, depthWrite: false, side: THREE.DoubleSide });
  const disc = new THREE.Mesh(discGeo, discMat); disc.rotation.x = -Math.PI / 2; disc.position.y = -0.4; group.add(disc);

  // ---------- rings ----------
  const rings = [];
  for (let i = 0; i < 5; i++) {
    const col = new THREE.Color(RING_COL[i]), r = RING_R[i];
    const g = new THREE.Group(); group.add(g);
    const core = new THREE.Mesh(new THREE.TorusGeometry(r, 0.06, 8, 240), new THREE.MeshBasicMaterial({ color: col, transparent: true, opacity: 0, blending: THREE.AdditiveBlending, depthWrite: false }));
    const halo = new THREE.Mesh(new THREE.TorusGeometry(r, 0.34, 10, 200), new THREE.MeshBasicMaterial({ color: col, transparent: true, opacity: 0, blending: THREE.AdditiveBlending, depthWrite: false }));
    core.rotation.x = halo.rotation.x = Math.PI / 2; g.add(halo); g.add(core);
    // accent dots
    const dotN = 16, dg = new THREE.BufferGeometry(), dp = new Float32Array(dotN * 3);
    for (let k = 0; k < dotN; k++) { const a = (k / dotN) * 6.2832 + rnd() * 0.1; dp[k * 3] = Math.cos(a) * r; dp[k * 3 + 1] = 0; dp[k * 3 + 2] = Math.sin(a) * r; }
    dg.setAttribute('position', new THREE.BufferAttribute(dp, 3));
    const dots = new THREE.Points(dg, new THREE.PointsMaterial({ color: col, size: 1.0, transparent: true, opacity: 0, blending: THREE.AdditiveBlending, depthWrite: false, sizeAttenuation: true }));
    g.add(dots);
    // sweeping scan head that rides the ring
    const scan = addGlow(group, RING_COL[i], 3.2, 0);
    rings.push({ i, r, col, core, halo, dots, scan, ph: rnd() * 6.28, scanA: rnd() * 6.28, scanSpd: 0.18 + rnd() * 0.16 });
  }

  // ---------- core ----------
  const center = new THREE.Group(); group.add(center);
  const atmo = addGlow(center, '#1f6bff', 22, 0);
  const atmo2 = addGlow(center, '#3fc9ff', 12, 0);
  const corona = sprite(center, CORONA, '#bdecff', 9, 0);
  const coreMesh = new THREE.Mesh(new THREE.IcosahedronGeometry(0.98, 2), new THREE.MeshBasicMaterial({ color: 0x9be9ff, transparent: true, opacity: 0 }));
  center.add(coreMesh);
  // fresnel-ish shell (back-side, additive) for a rim-lit orb
  const shell = new THREE.Mesh(new THREE.IcosahedronGeometry(1.5, 2), new THREE.MeshBasicMaterial({ color: 0x39a8ff, transparent: true, opacity: 0, blending: THREE.AdditiveBlending, depthWrite: false, side: THREE.BackSide, wireframe: true }));
  center.add(shell);
  const coreGlow = addGlow(center, '#22d3ee', 6, 0);
  const pulse1 = new THREE.Mesh(new THREE.TorusGeometry(1.8, 0.045, 8, 80), new THREE.MeshBasicMaterial({ color: 0x39d8ff, transparent: true, opacity: 0, blending: THREE.AdditiveBlending, depthWrite: false }));
  const pulse2 = new THREE.Mesh(new THREE.TorusGeometry(2.6, 0.03, 8, 80), new THREE.MeshBasicMaterial({ color: 0x7dd9ff, transparent: true, opacity: 0, blending: THREE.AdditiveBlending, depthWrite: false }));
  pulse1.rotation.x = pulse2.rotation.x = Math.PI / 2; center.add(pulse1); center.add(pulse2);

  // ---------- planets ----------
  const PER_RING = [2, 3, 2, 3, 3];
  const planets = [];
  for (let i = 0; i < 5; i++) {
    const col = new THREE.Color(RING_COL[i]), r = RING_R[i];
    for (let k = 0; k < PER_RING[i]; k++) {
      const a0 = rnd() * 6.2832;
      const big = rnd() < 0.45;
      const rad = (big ? 0.75 : 0.44) + rnd() * (big ? 0.5 : 0.2) + i * 0.05;
      const mesh = new THREE.Mesh(new THREE.SphereGeometry(rad, 22, 22), new THREE.MeshBasicMaterial({ color: col, transparent: true, opacity: 0 }));
      group.add(mesh);
      const halo = addGlow(group, RING_COL[i], rad * 6.5, 0);
      let sat = null;
      if (big && rnd() < 0.6) {
        sat = new THREE.Mesh(new THREE.TorusGeometry(rad * 1.7, rad * 0.12, 8, 48), new THREE.MeshBasicMaterial({ color: col, transparent: true, opacity: 0, blending: THREE.AdditiveBlending, depthWrite: false }));
        sat.rotation.x = Math.PI / 2 - 0.5 - rnd() * 0.5; sat.rotation.y = rnd(); group.add(sat);
      }
      planets.push({ ringIndex: i, r, angle: a0, speed: (0.05 + rnd() * 0.09) * (rnd() < 0.5 ? 1 : -1), mesh, halo, sat, col, rad, ph: rnd() * 6.28, big });
    }
  }

  // ---------- order-flow cables ----------
  const cables = []; const SEG = 40;
  function makeCable(getA, getB, colorHex) {
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.BufferAttribute(new Float32Array((SEG + 1) * 3), 3));
    const line = new THREE.Line(geo, new THREE.LineBasicMaterial({ color: new THREE.Color(colorHex), transparent: true, opacity: 0, blending: THREE.AdditiveBlending, depthWrite: false }));
    group.add(line);
    cables.push({ getA, getB, geo, line, phase: rnd() * 6.28, amp: 0.9 + rnd() * 1.4 });
  }
  const C0 = new THREE.Vector3(0, 0, 0);
  const shuffled = planets.slice().sort(() => rnd() - 0.5);
  for (let k = 0; k < 5; k++) { const p = shuffled[k]; if (p) makeCable(() => p.mesh.position, () => C0, '#' + p.col.getHexString()); }
  for (let k = 0; k < 4; k++) { const a = shuffled[k], b = shuffled[(k + 3) % shuffled.length]; if (a && b && a !== b) makeCable(() => a.mesh.position, () => b.mesh.position, rnd() < 0.5 ? '#2fe08a' : '#ff2f4d'); }

  // ---------- state / choreography ----------
  let introP = 0, exitP = 0, pxN = 0, pyN = 0, camX = 0, camY = 0;
  const BASE = { phi: 1.04, dist: 60, ty: -1 };
  const vA = new THREE.Vector3(), vB = new THREE.Vector3(), perp = new THREE.Vector3(), up = new THREE.Vector3(0, 1, 0), mid = new THREE.Vector3();

  function update(time) {
    const ip = easeInOutCubic(introP), fade = 1 - smooth(0, 1, exitP);
    const dist = BASE.dist + (122 - BASE.dist) * (1 - easeOutCubic(introP)) + exitP * 64;
    const phi = BASE.phi - exitP * 0.32;
    camX += ((pxN * 6) - camX) * 0.045; camY += ((-pyN * 4) - camY) * 0.045;
    const ty = BASE.ty;
    camera.position.set(camX, ty + dist * Math.cos(phi) + camY, dist * Math.sin(phi));
    camera.lookAt(0, ty, 0);

    group.rotation.y = time * 0.045 + exitP * 0.55;
    starsFar.rotation.y = time * 0.008; starsNear.rotation.y = -time * 0.012;

    const ringsIn = smooth(0.05, 0.7, introP);
    const planetsIn = smooth(0.32, 1, introP);
    const cablesIn = smooth(0.58, 1, introP);

    starsFar.material.opacity = starsFar.userData.baseOpa * ip * fade;
    starsNear.material.opacity = starsNear.userData.baseOpa * ip * fade;
    dust.material.opacity = 0.30 * ip * fade;
    nebulae[0].material.opacity = 0.17 * ip * fade;
    nebulae[1].material.opacity = 0.13 * ip * fade;
    nebulae[2].material.opacity = 0.10 * ip * fade;
    disc.material.opacity = 0.26 * ringsIn * fade;

    for (const rg of rings) {
      const br = 0.78 + 0.22 * Math.sin(time * 0.9 + rg.ph);
      rg.core.material.opacity = 0.98 * ringsIn * br * fade;
      rg.halo.material.opacity = 0.16 * ringsIn * br * fade;
      rg.dots.material.opacity = 0.9 * ringsIn * br * fade;
      const sc = 0.9 + 0.1 * easeOutCubic(ringsIn);
      rg.core.scale.setScalar(sc); rg.halo.scale.setScalar(sc); rg.dots.scale.setScalar(sc);
      // scan head rides the ring
      rg.scanA += rg.scanSpd * 0.016;
      rg.scan.position.set(Math.cos(rg.scanA) * rg.r, 0, Math.sin(rg.scanA) * rg.r);
      rg.scan.material.opacity = 0.9 * ringsIn * fade * (0.5 + 0.5 * Math.abs(Math.sin(time * 1.3 + rg.ph)));
    }

    // core
    const cb = 0.6 + 0.4 * Math.abs(Math.sin(time * 1.6));
    atmo.material.opacity = 0.30 * ringsIn * fade * (0.85 + 0.15 * Math.sin(time * 0.8));
    atmo2.material.opacity = 0.40 * ringsIn * fade * cb;
    corona.material.opacity = 0.7 * ringsIn * fade * (0.7 + 0.3 * Math.sin(time * 1.1));
    corona.material.rotation = time * 0.06;
    coreMesh.material.opacity = 0.95 * ringsIn * fade;
    coreMesh.rotation.y = time * 0.5; coreMesh.rotation.x = time * 0.3;
    coreMesh.scale.setScalar(1 + 0.12 * Math.sin(time * 2.2));
    shell.material.opacity = 0.4 * ringsIn * fade * (0.6 + 0.4 * Math.sin(time * 1.4 + 1));
    shell.rotation.y = -time * 0.22; shell.rotation.z = time * 0.16;
    coreGlow.material.opacity = 0.7 * ringsIn * fade * cb;
    pulse1.material.opacity = 0.55 * ringsIn * fade * cb;
    pulse1.scale.setScalar(1 + 0.18 * Math.sin(time * 1.6));
    pulse2.material.opacity = 0.32 * ringsIn * fade * (0.5 + 0.5 * Math.sin(time * 1.2 + 1.5));
    pulse2.scale.setScalar(1 + 0.12 * Math.sin(time * 1.1 + 0.8));

    // planets
    for (const p of planets) {
      p.angle += p.speed * 0.016;
      const x = Math.cos(p.angle) * p.r, z = Math.sin(p.angle) * p.r;
      p.mesh.position.set(x, 0, z); p.halo.position.set(x, 0, z);
      if (p.sat) p.sat.position.set(x, 0, z);
      const pulse = 0.6 + 0.4 * Math.abs(Math.sin(time * 1.5 + p.ph));
      p.mesh.material.opacity = planetsIn * fade;
      p.halo.material.opacity = (p.big ? 0.6 : 0.42) * planetsIn * fade * pulse;
      if (p.sat) p.sat.material.opacity = 0.5 * planetsIn * fade;
      p.mesh.scale.setScalar(1 + (p.big ? 0.08 : 0.05) * Math.sin(time * 1.5 + p.ph));
    }

    // cables
    for (const cab of cables) {
      vA.copy(cab.getA()); vB.copy(cab.getB());
      perp.subVectors(vB, vA); const len = perp.length() || 1;
      perp.normalize(); perp.cross(up).normalize();
      const arr = cab.geo.attributes.position.array;
      for (let s = 0; s <= SEG; s++) {
        const t2 = s / SEG; mid.lerpVectors(vA, vB, t2);
        const env = Math.sin(t2 * Math.PI);
        const wave = Math.sin(t2 * Math.PI * 3 - time * 2.6 + cab.phase) * cab.amp * 0.03 * len * env;
        mid.addScaledVector(perp, wave); mid.y += Math.sin(t2 * Math.PI * 2 + time * 1.5) * 0.42 * env;
        arr[s * 3] = mid.x; arr[s * 3 + 1] = mid.y; arr[s * 3 + 2] = mid.z;
      }
      cab.geo.attributes.position.needsUpdate = true;
      cab.line.material.opacity = (0.34 + 0.18 * Math.sin(time * 1.2 + cab.phase)) * cablesIn * fade;
    }

    mount.style.opacity = String(1 - smooth(0.74, 1, exitP));
    if (composer) composer.render(); else renderer.render(scene, camera);
  }

  // ---------- loop ----------
  let raf = 0, t0 = performance.now(), introStart = performance.now(), dead = false, visible = true, fc = 0;
  const INTRO_MS = reduce ? 200 : 2100;
  function frame(now) {
    if (dead) return;
    raf = requestAnimationFrame(frame);
    // self-heal size ~2×/sec — robust against ResizeObserver timing / 0-width
    // mounts, without forcing a layout reflow on every frame.
    if ((fc++ & 31) === 0 && (mount.clientWidth !== w || mount.clientHeight !== h)) onResize();
    // keep the rAF alive but skip GPU work while the hero is scrolled off-screen
    if (!visible) return;
    introP = Math.min(1, (now - introStart) / INTRO_MS);
    update((now - t0) / 1000);
  }

  // skip rendering work when off-screen (loop stays alive so it self-heals + resumes)
  const io = new IntersectionObserver(([e]) => { visible = e.isIntersecting; }, { threshold: 0.01 });
  io.observe(mount);

  // pointer parallax
  const onPointer = (ev) => {
    const r = mount.getBoundingClientRect();
    pxN = ((ev.clientX - r.left) / r.width - 0.5) * 2;
    pyN = ((ev.clientY - r.top) / r.height - 0.5) * 2;
  };
  window.addEventListener('pointermove', onPointer, { passive: true });

  function onResize() {
    w = mount.clientWidth || 1; h = mount.clientHeight || 1;
    camera.aspect = w / h; camera.updateProjectionMatrix();
    renderer.setSize(w, h);
    if (composer) composer.setSize(w, h);
  }
  const ro = new ResizeObserver(onResize); ro.observe(mount);

  raf = requestAnimationFrame(frame);

  return {
    setExit(p) { exitP = Math.max(0, Math.min(1, p)); },
    dispose() {
      dead = true; if (raf) cancelAnimationFrame(raf);
      io.disconnect(); ro.disconnect();
      window.removeEventListener('pointermove', onPointer);
      scene.traverse((o) => { if (o.geometry) o.geometry.dispose(); if (o.material) { const m = o.material; (Array.isArray(m) ? m : [m]).forEach((x) => x.dispose()); } });
      GLOW.dispose(); CORONA.dispose();
      if (composer) { try { composer.dispose?.(); } catch { /* noop */ } }
      if (renderer.domElement.parentNode) renderer.domElement.parentNode.removeChild(renderer.domElement);
      try { renderer.forceContextLoss(); } catch { /* noop */ }
      renderer.dispose();
    },
  };
}

export default createHeroPrism;
